import { useEffect, useState } from "react";
import { capitalize } from "../../../helpers/capitalize";
import { normalizeRanges } from "../../../helpers/availability";
import moment from "moment-timezone";
import { getEvents } from "../../../helpers/google";
import { SpinnerCircular } from "spinners-react";
import { toast } from "react-toastify";
import { motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

function isAvailableDateSlot(
    date,
    availableSlots,
    timezone,
    reservationPeriod,
) {
    // Si la date est dans le passé (on compare au niveau du jour, pas de l'heure)
    if (
        moment.utc(date).tz(timezone).startOf("day").isBefore(
            moment.utc().tz(timezone).startOf("day"),
        )
    ) {
        return false;
    }

    // Si la date est au-delà de la limite de jours "indefinitely", "30days" et "14days"
    if (reservationPeriod != "indefinitely") {
        if (reservationPeriod == "30days") {
            if (
                moment.utc(date).tz(timezone) >
                moment.utc().tz(timezone).add(30, "days")
            ) {
                return false;
            }
        } else if (reservationPeriod == "14days") {
            if (
                moment.utc(date).tz(timezone) >
                moment.utc().tz(timezone).add(14, "days")
            ) {
                return false;
            }
        }
    }

    // Si la date est dans un slot disponible
    if (availableSlots[date.format("YYYY-MM-DD")]) {
        const availableSlotsArray =
            availableSlots[date.format("YYYY-MM-DD")].slots;
        if (availableSlotsArray.length > 0) {
            return true;
        }
    }

    return false;
}

/**
 * Transforme les événements Google bruts en intervalles « occupés », en
 * écartant ceux qui ne traduisent PAS une indisponibilité :
 *  - lieu de travail (« Domicile »/« Bureau ») et anniversaires (all-day infos)
 *  - événements marqués « Disponible » (transparency libre)
 *  - événements annulés
 * Conserve le titre (event.summary) pour pouvoir l'afficher côté mentor.
 */
function buildBusyEvents(eventsFromGoogle, timezone) {
    return (eventsFromGoogle || [])
        .filter((event) => {
            if (!event || !event.start) return false;
            if (event.status === "cancelled") return false;
            if (event.transparency === "transparent") return false;
            if (
                event.eventType === "workingLocation" ||
                event.eventType === "birthday"
            ) {
                return false;
            }
            return true;
        })
        .map((event) => {
            const title = event.summary || "";
            if (event.start.dateTime) {
                return {
                    title,
                    start: moment.utc(event.start.dateTime).tz(timezone),
                    end: moment.utc(event.end.dateTime).tz(timezone),
                };
            }
            // Journée entière : start/end.date (fin exclusive chez Google).
            return {
                title,
                start: moment
                    .tz(event.start.date, "YYYY-MM-DD", timezone)
                    .startOf("day"),
                end: moment
                    .tz(event.end.date, "YYYY-MM-DD", timezone)
                    .startOf("day"),
            };
        });
}

/**
 * Mode mentor : pour chaque jour À VENIR sans créneau disponible, détermine la
 * RAISON afin de l'expliquer au mentor. Lecture seule, n'altère pas la
 * génération des créneaux. Renvoie { "YYYY-MM-DD": { type, events? } } avec
 * type ∈ "weekly" | "exception" | "calendar" | "window" | "notice".
 */
function computeDayReasons({
    days,
    mentor,
    eventsFromGoogle,
    availableSlots,
    selectedTimezone,
    maximumHoursWithNewCall,
}) {
    const reasons = {};
    const overrides = mentor.mentorDateOverrides || {};
    const busy = buildBusyEvents(eventsFromGoogle, mentor.timezone);
    const todayStart = moment().tz(selectedTimezone).startOf("day");

    for (const day of days) {
        if (!day) continue;
        const dateKey = day.format("YYYY-MM-DD");

        // Jour avec créneaux -> rien à expliquer.
        if (
            availableSlots[dateKey] &&
            availableSlots[dateKey].slots.length > 0
        ) {
            continue;
        }
        // Jour passé -> non pertinent (déjà grisé).
        if (day.clone().startOf("day").isBefore(todayStart)) continue;

        // Origine des horaires du jour : exception de date ou horaire hebdo.
        const hasOverride = Object.prototype.hasOwnProperty.call(
            overrides,
            dateKey,
        );
        let ranges;
        if (hasOverride) {
            ranges = normalizeRanges(overrides[dateKey]);
            if (ranges.length === 0) {
                reasons[dateKey] = { type: "exception" };
                continue;
            }
        } else {
            moment.locale("en");
            const dayKey = `mentor${capitalize(moment.weekdays(day.day()))}`;
            moment.locale("fr");
            ranges = normalizeRanges(mentor[dayKey]);
            if (ranges.length === 0) {
                reasons[dateKey] = { type: "weekly" };
                continue;
            }
        }

        // Au-delà de la fenêtre de réservation.
        if (
            mentor.reservationPeriod === "30days" &&
            day.isAfter(moment().tz(selectedTimezone).add(30, "days"))
        ) {
            reasons[dateKey] = { type: "window" };
            continue;
        }
        if (
            mentor.reservationPeriod === "14days" &&
            day.isAfter(moment().tz(selectedTimezone).add(14, "days"))
        ) {
            reasons[dateKey] = { type: "window" };
            continue;
        }
        if (
            maximumHoursWithNewCall &&
            moment().add(maximumHoursWithNewCall, "days").isBefore(day)
        ) {
            reasons[dateKey] = { type: "window" };
            continue;
        }

        // Le jour a des horaires mais aucun créneau : on cherche les événements
        // de l'agenda qui recouvrent ces plages (titres pour le mentor).
        const dayDate = day.date();
        const overlappingTitles = [];
        for (const [start, end] of ranges) {
            const rangeStart = moment.tz(
                `${day.year()}-${day.month() + 1}-${dayDate} ${start}`,
                "YYYY-MM-DD HH:mm",
                mentor.timezone,
            );
            const rangeEnd = moment.tz(
                `${day.year()}-${day.month() + 1}-${dayDate} ${end}`,
                "YYYY-MM-DD HH:mm",
                mentor.timezone,
            );
            for (const ev of busy) {
                if (
                    ev.start.isBefore(rangeEnd) &&
                    ev.end.isAfter(rangeStart)
                ) {
                    const title = ev.title || "";
                    if (!overlappingTitles.includes(title)) {
                        overlappingTitles.push(title);
                    }
                }
            }
        }
        if (overlappingTitles.length > 0) {
            reasons[dateKey] = { type: "calendar", events: overlappingTitles };
        } else {
            // Horaires présents, pas d'événement : délai mini / journée entamée.
            reasons[dateKey] = { type: "notice" };
        }
    }

    return reasons;
}

export default function Calendar(props) {
    const t = useTranslations();

    const [selectedDate, setSelectedDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [userCanGoPast, setUserCanGoPast] = useState(false);
    const [creating, setCreating] = useState(false);
    const [days, setDays] = useState([]);
    const [bookedEvents, setBookedEvents] = useState([]);
    // Timezone CONTRÔLÉE par le parent (BookNewCall) : permet d'afficher le
    // sélecteur ailleurs (ex : colonne hôte du flux prospection, façon Cal.com).
    const selectedTimezone =
        props.selectedTimezone || (props.mentee && props.mentee.timezone);
    const [slots, setSlots] = useState([]);
    // Mode mentor : raison d'indisponibilité par date + date sélectionnée pour
    // afficher le détail (pourquoi ce jour n'a aucun créneau).
    const [dayReasons, setDayReasons] = useState({});
    const [selectedReasonKey, setSelectedReasonKey] = useState(null);

    const locale = useLocale();

    let month = capitalize(
        currentMonth.toLocaleDateString(locale, {
            month: "long",
            year: "numeric",
        }),
    );

    const daysInMonth = (month, year) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const firstDayOfMonth = (month, year) => {
        return new Date(year, month, 0).getDay();
    };

    const createArray = async (month = currentMonth) => {
        if (!creating) {
            setCreating(true);
            const days = [];
            const daysInCurrMonth = daysInMonth(
                month.getMonth(),
                month.getFullYear(),
            );
            const firstDay = firstDayOfMonth(
                month.getMonth(),
                month.getFullYear(),
            );

            for (let i = 1; i <= firstDay; i++) {
                days.push(null);
            }

            for (let i = 1; i <= daysInCurrMonth; i++) {
                // On construit chaque jour comme une date calendaire pure dans la
                // timezone du visiteur (selectedTimezone), pour que la grille, le
                // regroupement des créneaux et l'affichage soient parfaitement alignés.
                // (Sinon décalage d'un jour dès que le visiteur change de fuseau.)
                days.push(
                    moment.tz(
                        `${month.getFullYear()}-${month.getMonth() + 1}-${i}`,
                        "YYYY-M-D",
                        selectedTimezone,
                    ),
                );
            }

            const daysLeft = 7 - (days.length % 7);
            for (let i = 0; i < daysLeft; i++) {
                days.push(null);
            }

            // Maintenant qu'on a les dates du mois on va créer les slots disponibles pour chaque jour selon la timezone du mentor
            // On va créer des slots pour chaque jour avec un mentorMonday[0] de début et un mentorMonday[1] de fin, puis pareil pour mardi, etc.
            // Chaque slot aura une durée de minutesPerCall minutes
            // Les slots disponibles sur un même jour auront une date moment de début et une date moment de fin
            // On stockera ensuite chaque slot dans un tableau de slots disponibles pour le jour du mois : days = { day: moment, slots: [ { start: moment, end: moment } ]]}

            // On récupère les événements du calendrier Google du mentor
            let eventsFromGoogle = [];
            if (props.mentor.googleCalendarConnected) {
                const events = await getEvents(props.mentor.id, month);
                if (events) {
                    eventsFromGoogle = events;
                    setBookedEvents(events);
                } else {
                    toast.error(
                        "Impossible de récupérer les événements du calendrier Google pour le moment.",
                    );
                }
            } else {
                setBookedEvents([]);
            }

            // On crée pour tous les slots disponibles un tableau
            let slots = [];

            // Pour chaque jour du mois
            for (let i = 0; i < days.length; i++) {
                // On récupère le jour du mois
                const day = days[i];

                // Si le jour est null, on passe au jour suivant
                if (day === null) {
                    continue;
                }

                // On récupère la date du jour du mois
                const dayDate = day.date();

                // On récupère le jour de la semaine du jour du mois
                const dayWeekday = day.day();

                // Si le mentor est pré-enregistré : il doit être onboardé pour pouvoir enregistrer des appels
                if (!props.mentor.isOnboarded) {
                    continue;
                }

                // Exceptions de date : une exception définie pour cette date
                // précise prime sur l'horaire hebdomadaire (jour off ou
                // horaires spéciaux) et peut même ouvrir un jour normalement
                // non travaillé. On la résout donc avant les gardes hebdo.
                const dateKey = day.format("YYYY-MM-DD");
                const dateOverrides = props.mentor.mentorDateOverrides || {};
                const hasOverride = Object.prototype.hasOwnProperty.call(
                    dateOverrides,
                    dateKey,
                );

                // En l'absence d'exception, on vérifie la disponibilité
                // hebdomadaire (mentorMonday, etc.).
                if (!hasOverride) {
                    if (!props.mentor.mentorMonday && dayWeekday == 1) {
                        continue;
                    }
                    if (!props.mentor.mentorTuesday && dayWeekday == 2) {
                        continue;
                    }
                    if (!props.mentor.mentorWednesday && dayWeekday == 3) {
                        continue;
                    }
                    if (!props.mentor.mentorThursday && dayWeekday == 4) {
                        continue;
                    }
                    if (!props.mentor.mentorFriday && dayWeekday == 5) {
                        continue;
                    }
                    if (!props.mentor.mentorSaturday && dayWeekday == 6) {
                        continue;
                    }
                    if (!props.mentor.mentorSunday && dayWeekday == 0) {
                        continue;
                    }
                }

                // Si props.maximumDaysWithNewCall existe, on compare la date du jour avec la date du jour + maximumDaysWithNewCall
                // Si la date du jour est supérieure à la date du jour + maximumDaysWithNewCall, on passe au jour suivant
                if (
                    props.maximumHoursWithNewCall &&
                    moment().add(props.maximumHoursWithNewCall, "days") < day
                ) {
                    continue;
                }

                // On récupère la timezone du mentor
                const mentorTimezone = props.mentor.timezone;

                // On crée pour le jour du mois un tableau de slots disponibles
                // days[i].slots = [];

                moment.locale("en");
                // Clé du jour (mentorMonday, mentorTuesday, ...).
                const dayKey = `mentor${capitalize(
                    moment.weekdays(dayWeekday),
                )}`;
                moment.locale("fr");

                // Un jour peut contenir plusieurs plages horaires (ex : 9h-12h
                // ET 14h-18h). En présence d'une exception de date, ce sont ses
                // plages qui s'appliquent ([] = jour off). normalizeRanges gère
                // aussi l'ancien format à une seule plage ["09:00","17:00"].
                const dayRanges = hasOverride
                    ? normalizeRanges(dateOverrides[dateKey])
                    : normalizeRanges(props.mentor[dayKey]);
                if (dayRanges.length === 0) {
                    continue;
                }

                // On récupère la durée de chaque slot en minutes.
                // Garde-fou CRITIQUE : une durée nulle, négative ou non
                // numérique ferait tourner la boucle de génération des créneaux
                // à l'infini (j.add(0, "minutes") ne fait jamais avancer j),
                // saturant la mémoire et gelant l'onglet. Dans ce cas, ce mentor
                // n'a tout simplement aucun créneau réservable : on saute le jour
                // (même logique que les autres `continue` ci-dessus).
                const minutesPerCall = parseInt(
                    props.numberOfMinutesPerCall || props.mentor.minutesPerCall,
                    10,
                );
                if (!Number.isFinite(minutesPerCall) || minutesPerCall <= 0) {
                    continue;
                }

                // Pour chaque plage du jour, on la découpe en créneaux de
                // minutesPerCall minutes.
                for (const [mentorDayStart, mentorDayEnd] of dayRanges) {
                    const mentorDayStartMoment = moment.tz(
                        `${day.year()}-${day.month() + 1}-${dayDate} ${
                            mentorDayStart.split(":")[0]
                        }:${mentorDayStart.split(":")[1]}`,
                        "YYYY-MM-DD HH:mm",
                        mentorTimezone,
                    );
                    const mentorDayEndMoment = moment.tz(
                        `${day.year()}-${day.month() + 1}-${dayDate} ${
                            mentorDayEnd.split(":")[0]
                        }:${mentorDayEnd.split(":")[1]}`,
                        "YYYY-MM-DD HH:mm",
                        mentorTimezone,
                    );

                    // On crée un slot pour chaque tranche de minutesPerCall
                    // minutes entre le début et la fin de la plage.
                    for (
                        let j = mentorDayStartMoment;
                        j.isBefore(mentorDayEndMoment);
                        j.add(minutesPerCall, "minutes")
                    ) {
                        slots.push({
                            start: j.clone(),
                            end: j.clone().add(minutesPerCall, "minutes"),
                        });
                    }
                }

                let timeBeforeReservation = 0;
                if (props.mentor.timeBeforeReservation) {
                    const timeBeforeReservationArray =
                        props.mentor.timeBeforeReservation.split(":");
                    timeBeforeReservation +=
                        parseInt(timeBeforeReservationArray[0]) * 60;
                    timeBeforeReservation +=
                        parseInt(timeBeforeReservationArray[1]) * 1;
                }

                let timeAfterReservation = 0;
                if (props.mentor.timeAfterReservation) {
                    const timeAfterReservationArray =
                        props.mentor.timeAfterReservation.split(":");
                    timeAfterReservation +=
                        parseInt(timeAfterReservationArray[0]) * 60;
                    timeAfterReservation +=
                        parseInt(timeAfterReservationArray[1]) * 1;
                }

                // Événements occupés du calendrier Google (lieux de travail,
                // anniversaires, événements « Disponible » et annulés exclus).
                const busySlots = buildBusyEvents(
                    eventsFromGoogle,
                    props.mentor.timezone,
                );

                // On filtre les slots disponibles pour ne garder que ceux qui ne sont pas occupés par un événement du calendrier Google
                slots = slots.filter((slot) => {
                    let isSlotFree = true;
                    for (let j = 0; j < busySlots.length; j++) {
                        let tempStart = slot.start
                            .clone()
                            .subtract(timeBeforeReservation, "minutes");
                        let tempEnd = slot.end
                            .clone()
                            .add(timeAfterReservation, "minutes");
                        if (
                            tempStart.isBetween(
                                busySlots[j].start,
                                busySlots[j].end,
                                null,
                                "[)",
                            ) ||
                            tempEnd.isBetween(
                                busySlots[j].start,
                                busySlots[j].end,
                                null,
                                "(]",
                            ) ||
                            busySlots[j].start.isBetween(
                                tempStart,
                                tempEnd,
                                null,
                                "[)",
                            )
                        ) {
                            isSlotFree = false;
                        }
                    }
                    return isSlotFree;
                });
            }

            // Transforme tous les slots dans la timezone du visiteur (selectedTimezone)
            for (let i = 0; i < slots.length; i++) {
                slots[i].start = slots[i].start.tz(selectedTimezone);
                slots[i].end = slots[i].end.tz(selectedTimezone);
            }

            // On vérifie qu'il y a bien un délai d'au moins x heures entre maintenant et chaque slot
            slots = slots.filter((slot) => {
                return slot.start.isAfter(
                    moment()
                        .tz(selectedTimezone)
                        .add(
                            props.mentor.mentorMinimumHoursOfNoticeForNewCall,
                            "hours",
                        ),
                );
            });

            // Regroupe les slots par jour
            const availableSlots = slots.reduce((acc, slot) => {
                const day = slot.start.format("YYYY-MM-DD");
                if (!acc[day]) {
                    acc[day] = {
                        date: slot.start,
                        slots: [slot],
                    };
                } else {
                    acc[day].slots.push(slot);
                }
                return acc;
            }, {});

            props.setSlots(availableSlots);

            // Mode mentor : on explique chaque jour à venir resté sans créneau.
            if (props.mentorMode) {
                setDayReasons(
                    computeDayReasons({
                        days,
                        mentor: props.mentor,
                        eventsFromGoogle,
                        availableSlots,
                        selectedTimezone,
                        maximumHoursWithNewCall: props.maximumHoursWithNewCall,
                    }),
                );
                setSelectedReasonKey(null);
            }

            setCreating(false);
            setDays(days);
            setSlots(availableSlots);
        }
    };

    const handleDateClick = (date) => {
        if (
            isAvailableDateSlot(
                date,
                slots,
                selectedTimezone,
                props.mentor.reservationPeriod,
            )
        ) {
            props.selectedDate(date);
            setSelectedDate(date);
        }
    };

    const handlePrevMonth = () => {
        if (userCanGoPast) {
            let prevMonth = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() - 1,
                1,
            );
            // Now
            let now = new Date();
            // format with first day of month
            now = new Date(now.getFullYear(), now.getMonth(), 1);

            let prevPrevMonth = new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() - 2,
                1,
            );

            if (prevMonth >= now) {
                setCurrentMonth(prevMonth);
            }

            if (prevPrevMonth >= now) {
                setUserCanGoPast(true);
            } else {
                setUserCanGoPast(false);
            }

            createArray(prevMonth);
        }
    };

    const handleNextMonth = () => {
        const nextMonth = new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth() + 1,
        );
        if (nextMonth >= new Date()) {
            setUserCanGoPast(true);
            setCurrentMonth(nextMonth);
        }

        createArray(nextMonth);
    };

    const isToday = (date) => {
        const today = moment.utc().tz(selectedTimezone);
        return (
            date.date() === today.date() &&
            date.month() === today.month() &&
            date.year() === today.year()
        );
    };

    useEffect(() => {
        createArray();
    }, []);

    useEffect(() => {
        createArray();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimezone]);

    const weekdayKeys = [
        "Calendar_week_mon",
        "Calendar_week_tue",
        "Calendar_week_wed",
        "Calendar_week_thu",
        "Calendar_week_fri",
        "Calendar_week_sat",
        "Calendar_week_sun",
    ];

    return (
        <div className="space-y-5">
            {/* Calendar Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white capitalize">
                    {month}
                </h3>
                <div className="flex items-center gap-1">
                    <button
                        onClick={handlePrevMonth}
                        disabled={!userCanGoPast}
                        aria-label="Mois précédent"
                        className={`h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${
                            userCanGoPast
                                ? "text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:text-grayDark-500 dark:hover:text-grayDark-200 dark:hover:bg-box"
                                : "text-gray-200 dark:text-grayDark-700 cursor-not-allowed"
                        }`}
                    >
                        <SolarIcon
                            name="alt-arrow-left-linear"
                            className="text-lg"
                        />
                    </button>

                    <button
                        onClick={handleNextMonth}
                        aria-label="Mois suivant"
                        className="h-9 w-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:text-grayDark-500 dark:hover:text-grayDark-200 dark:hover:bg-box transition-colors"
                    >
                        <SolarIcon
                            name="alt-arrow-right-linear"
                            className="text-lg"
                        />
                    </button>
                </div>
            </div>

            {/* Calendar Grid */}
            {creating ? (
                <div className="flex justify-center items-center h-72">
                    <SpinnerCircular
                        size={38}
                        thickness={140}
                        color="#E8511D"
                        secondaryColor="rgba(232,81,29,0.12)"
                    />
                </div>
            ) : (
                <motion.table
                    key={month}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="w-full"
                >
                    <thead>
                        <tr>
                            {weekdayKeys.map((key) => (
                                <th
                                    key={key}
                                    className="pb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-grayDark-500"
                                >
                                    {t(key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {days
                            .reduce((rows, day, i) => {
                                if (i % 7 === 0) rows.push([]);
                                rows[rows.length - 1].push(day);
                                return rows;
                            }, [])
                            .map((row, i) => {
                                return (
                                    <tr key={i}>
                                        {row.map((day, j) => {
                                            if (day === null) {
                                                return (
                                                    <td key={j} className="py-1">
                                                        <div className="w-10 h-10 mx-auto" />
                                                    </td>
                                                );
                                            }
                                            const isAvailable = isAvailableDateSlot(
                                                day,
                                                slots,
                                                selectedTimezone,
                                                props.mentor.reservationPeriod,
                                            );
                                            const isTodayDate = isToday(day);
                                            const dateKey = day.format(
                                                "YYYY-MM-DD",
                                            );
                                            const isSelected =
                                                selectedDate &&
                                                dateKey ===
                                                    selectedDate.format(
                                                        "YYYY-MM-DD",
                                                    );
                                            // Mode mentor : jour à venir sans
                                            // créneau mais avec une raison connue.
                                            const reason =
                                                props.mentorMode && !isAvailable
                                                    ? dayReasons[dateKey]
                                                    : null;
                                            const hasReason = !!reason;
                                            const isReasonSelected =
                                                hasReason &&
                                                selectedReasonKey === dateKey;

                                            return (
                                                <td
                                                    key={j}
                                                    onClick={() => {
                                                        if (hasReason) {
                                                            // On retire la
                                                            // sélection noire d'un
                                                            // jour dispo précédent.
                                                            setSelectedDate(null);
                                                            setSelectedReasonKey(
                                                                isReasonSelected
                                                                    ? null
                                                                    : dateKey,
                                                            );
                                                        } else {
                                                            // Tout autre jour ferme
                                                            // le panneau de raison.
                                                            setSelectedReasonKey(
                                                                null,
                                                            );
                                                            handleDateClick(day);
                                                        }
                                                    }}
                                                    className="py-1"
                                                >
                                                    <div
                                                        className={`relative w-10 h-10 rounded-full mx-auto flex items-center justify-center text-sm transition-colors ${
                                                            isSelected
                                                                ? "bg-gray-900 text-white font-semibold dark:bg-white dark:text-grayDark-900 cursor-pointer"
                                                                : isAvailable
                                                                ? "text-gray-800 font-medium hover:bg-gray-100 dark:text-grayDark-200 dark:hover:bg-box cursor-pointer"
                                                                : isReasonSelected
                                                                ? "bg-amber-100 text-amber-700 font-medium dark:bg-amber-500/15 dark:text-amber-300 cursor-pointer"
                                                                : hasReason
                                                                ? "text-gray-400 hover:bg-gray-100 dark:text-grayDark-500 dark:hover:bg-box cursor-pointer"
                                                                : "text-gray-300 dark:text-grayDark-700 cursor-default"
                                                        }`}
                                                    >
                                                        {day.date()}
                                                        {isAvailable &&
                                                            !isSelected && (
                                                                <span
                                                                    className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                                                                        isTodayDate
                                                                            ? "bg-primary"
                                                                            : "bg-gray-300 dark:bg-grayDark-600"
                                                                    }`}
                                                                />
                                                            )}
                                                        {hasReason &&
                                                            !isReasonSelected && (
                                                                <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-400/80" />
                                                            )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                    </tbody>
                </motion.table>
            )}

            {/* Mode mentor : raison d'un jour sans créneau (clic sur le jour) */}
            {props.mentorMode &&
                (selectedReasonKey && dayReasons[selectedReasonKey] ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 dark:border-amber-500/25 dark:bg-amber-500/10">
                        <div className="flex items-start gap-2.5">
                            <SolarIcon
                                name="info-circle-linear"
                                className="mt-0.5 flex-shrink-0 text-base text-amber-500"
                            />
                            <div className="min-w-0">
                                <p className="mb-1 text-sm font-semibold capitalize text-gray-900 dark:text-white">
                                    {capitalize(
                                        moment(selectedReasonKey)
                                            .locale(locale)
                                            .format("dddd D MMMM"),
                                    )}
                                </p>
                                {dayReasons[selectedReasonKey].type ===
                                "calendar" ? (
                                    <p className="mb-0 text-sm text-gray-600 dark:text-grayDark-300">
                                        {t("Calendar_reason_calendar")}{" "}
                                        <span className="font-medium text-gray-800 dark:text-grayDark-100">
                                            {dayReasons[selectedReasonKey].events
                                                .map(
                                                    (e) =>
                                                        e ||
                                                        t(
                                                            "Calendar_reason_busy_generic",
                                                        ),
                                                )
                                                .join(", ")}
                                        </span>
                                    </p>
                                ) : (
                                    <p className="mb-0 text-sm text-gray-600 dark:text-grayDark-300">
                                        {t(
                                            `Calendar_reason_${dayReasons[selectedReasonKey].type}`,
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="mb-0 flex items-center gap-2 text-xs text-gray-400 dark:text-grayDark-500">
                        <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
                        {t("Calendar_reason_legend")}
                    </p>
                ))}

            {/* Sélecteur de fuseau (masqué quand le parent l'affiche ailleurs) */}
            {!props.hideTimezoneSelector && (
                <div className="flex items-center gap-1.5 text-gray-400 dark:text-grayDark-500 pt-1">
                    <SolarIcon
                        name="global-linear"
                        className="text-sm flex-shrink-0"
                    />
                    <div className="relative flex-1 min-w-0">
                        <select
                            className="appearance-none w-full text-xs font-medium text-gray-500 dark:text-grayDark-400 bg-transparent cursor-pointer focus:outline-hidden truncate p-0 pr-5"
                            value={selectedTimezone}
                            onChange={(e) =>
                                props.changeTimezone(e.target.value)
                            }
                        >
                            {moment.tz.names().map((timezone) => {
                                return (
                                    <option key={timezone} value={timezone}>
                                        {timezone} -{" "}
                                        {moment().tz(timezone).format("HH:mm")}
                                    </option>
                                );
                            })}
                        </select>
                        <SolarIcon
                            name="alt-arrow-down-linear"
                            className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-xs text-gray-400"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
